import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    TruckIcon,
    MapPinIcon,
    CalendarIcon,
    TicketIcon,
    CurrencyDollarIcon,
    XCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EllipsisVerticalIcon,
    NoSymbolIcon,
    PlayIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import { getRideOffers, cancelRideOffer, suspendRideOffer } from "../api/rideBookingsTripsApi";
import type { RideOffersQueryParams, RideOfferStatus, VehicleType } from "../types";

// ============================================================================
// Constants
// ============================================================================

const statusOptions: { value: RideOfferStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "AVAILABLE", label: "Available" },
    { value: "FULL", label: "Full" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "EXPIRED", label: "Expired" },
    { value: "SUSPENDED", label: "Suspended" },
];

const vehicleOptions: { value: VehicleType | ""; label: string }[] = [
    { value: "", label: "All Vehicles" },
    { value: "KEKE", label: "Keke" },
    { value: "REGULAR", label: "Regular" },
    { value: "COMFORT", label: "Comfort" },
    { value: "LUXURIOUS", label: "Luxurious" },
];

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        AVAILABLE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FULL: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        IN_PROGRESS: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        COMPLETED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        EXPIRED: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        SUSPENDED: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
};

const VehicleBadge = ({ type }: { type: string }) => {
    const t = type ?? "UNKNOWN";
    const styles: Record<string, string> = {
        KEKE: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        REGULAR: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        COMFORT: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        LUXURIOUS: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[t] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {t}
        </span>
    );
};

// ============================================================================
// Helper
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const RideOffersRoute = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [filters, setFilters] = useState<RideOffersQueryParams>({
        page: 0,
        limit: 20,
        status: "",
        vehicleType: "",
        search: "",
        dateFrom: "",
        dateTo: "",
    });
    const [showFilters, setShowFilters] = useState(false);
    const [actionMenu, setActionMenu] = useState<string | null>(null);
    const [cancelModal, setCancelModal] = useState<{ id: string; origin: string; destination: string } | null>(null);
    const [suspendModal, setSuspendModal] = useState<{ id: string; origin: string; destination: string; isSuspended: boolean } | null>(null);
    const [actionReason, setActionReason] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["rideOffers", filters],
        queryFn: () => getRideOffers(filters),
    });

    const cancelMutation = useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelRideOffer(id, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["rideOffers"] });
            setCancelModal(null);
            setActionReason("");
        },
    });

    const suspendMutation = useMutation({
        mutationFn: ({ id, suspend, reason }: { id: string; suspend: boolean; reason: string }) =>
            suspendRideOffer(id, suspend, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["rideOffers"] });
            setSuspendModal(null);
            setActionReason("");
        },
    });

    // Some API payloads may return ride list under `rideOffers` or `rides`.
    const rideOffers = data?.data?.rideOffers ?? data?.data?.rides ?? [];
    const totalPages = data?.data?.totalPages ?? 0;
    const totalElements = data?.data?.totalElements ?? 0;
    const currentPage = filters.page ?? 0;
    const canManageRideOffers = hasPermission(SECTION_PERMISSIONS.RIDE_OFFERS_MANAGE[0]);

    const updateFilter = (key: keyof RideOffersQueryParams, value: string) => {
        setFilters((prev) => ({ ...prev, [key]: value, page: 0 }));
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Ride Offers</h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {totalElements} ride offer{totalElements !== 1 ? "s" : ""} total
                </p>
            </div>

            {/* Search & Filters */}
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search by origin or destination..."
                            value={filters.search}
                            onChange={(e) => updateFilter("search", e.target.value)}
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500"
                        />
                    </div>
                    <select
                        value={filters.status}
                        onChange={(e) => updateFilter("status", e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                        {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <select
                        value={filters.vehicleType}
                        onChange={(e) => updateFilter("vehicleType", e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    >
                        {vehicleOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowFilters((p) => !p)}
                        className={showFilters ? "border-primary-500 text-primary-600" : ""}
                    >
                        <FunnelIcon className="h-4 w-4" />
                    </Button>
                </div>

                {showFilters && (
                    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">From</label>
                            <input
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => updateFilter("dateFrom", e.target.value)}
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">To</label>
                            <input
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => updateFilter("dateTo", e.target.value)}
                                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                            />
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFilters({ page: 0, limit: 20, status: "", vehicleType: "", search: "", dateFrom: "", dateTo: "" })}
                        >
                            Clear All
                        </Button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
                    </div>
                ) : error ? (
                    <div className="px-6 py-20 text-center">
                        <XCircleIcon className="mx-auto h-10 w-10 text-rose-400" />
                        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{extractApiError(error)}</p>
                    </div>
                ) : rideOffers.length === 0 ? (
                    <div className="px-6 py-20 text-center">
                        <TruckIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">No ride offers found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-800/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Route</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Driver</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Departure</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Vehicle</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Seats</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Price/Seat</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Revenue</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                                    {canManageRideOffers ? (
                                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Actions</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {rideOffers.map((ride) => (
                                    <tr
                                        key={ride.rideOfferId}
                                        onClick={() => navigate(`/ride-bookings-trips/rides/${ride.rideOfferId}`)}
                                        className="cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="max-w-[200px]">
                                                <div className="flex items-center gap-1.5">
                                                    <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{ride.origin || ride.originAddress}</p>
                                                </div>
                                                <div className="mt-0.5 flex items-center gap-1.5">
                                                    <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-rose-500" />
                                                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">{ride.destination || ride.destinationAddress}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{ride.driverName}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
                                                <CalendarIcon className="h-3.5 w-3.5" />
                                                {ride.departureDate ? `${ride.departureDate} ${ride.departureTime?.slice(0, 5) || ""}` : formatDate(ride.departureTime)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3"><VehicleBadge type={ride.vehicleType} /></td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <TicketIcon className="h-3.5 w-3.5 text-slate-400" />
                                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                    {ride.availableSeats ?? (ride.totalSeats - (ride.bookedSeats ?? 0))}/{ride.totalSeats}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                                            {formatCurrency(ride.pricePerSeat, ride.currency || ride.pricePerSeatCurrency)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <CurrencyDollarIcon className="h-3.5 w-3.5 text-emerald-500" />
                                                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                                                    {formatCurrency(ride.totalRevenue ?? ride.revenue ?? ride.totalFare, ride.currency || ride.totalFareCurrency)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge status={ride.status} /></td>
                                        {canManageRideOffers ? (
                                            <td className="px-4 py-3 text-right">
                                                <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => setActionMenu(actionMenu === ride.rideOfferId ? null : ride.rideOfferId)}
                                                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                                                    >
                                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                                    </button>
                                                    {actionMenu === ride.rideOfferId && (
                                                        <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                                                            <button
                                                                onClick={() => {
                                                                    navigate(`/ride-bookings-trips/rides/${ride.rideOfferId}`);
                                                                    setActionMenu(null);
                                                                }}
                                                                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                                                            >
                                                                <TruckIcon className="h-4 w-4" />
                                                                View Details
                                                            </button>
                                                            {ride.status === "SUSPENDED" ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setSuspendModal({ id: ride.rideOfferId, origin: ride.origin || ride.originAddress || "", destination: ride.destination || ride.destinationAddress || "", isSuspended: true });
                                                                        setActionMenu(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                                                >
                                                                    <PlayIcon className="h-4 w-4" />
                                                                    Resume Ride
                                                                </button>
                                                            ) : !["CANCELLED", "COMPLETED", "EXPIRED"].includes(ride.status) ? (
                                                                <button
                                                                    onClick={() => {
                                                                        setSuspendModal({ id: ride.rideOfferId, origin: ride.origin || ride.originAddress || "", destination: ride.destination || ride.destinationAddress || "", isSuspended: false });
                                                                        setActionMenu(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
                                                                >
                                                                    <NoSymbolIcon className="h-4 w-4" />
                                                                    Suspend Ride
                                                                </button>
                                                            ) : null}
                                                            {!["CANCELLED", "COMPLETED", "EXPIRED"].includes(ride.status) && (
                                                                <button
                                                                    onClick={() => {
                                                                        setCancelModal({ id: ride.rideOfferId, origin: ride.origin || ride.originAddress || "", destination: ride.destination || ride.destinationAddress || "" });
                                                                        setActionMenu(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                                >
                                                                    <XCircleIcon className="h-4 w-4" />
                                                                    Cancel Ride
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Page {currentPage + 1} of {totalPages}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={currentPage === 0}
                                onClick={() => setFilters((p) => ({ ...p, page: currentPage - 1 }))}
                            >
                                <ChevronLeftIcon className="mr-1 h-4 w-4" />
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={currentPage >= totalPages - 1}
                                onClick={() => setFilters((p) => ({ ...p, page: currentPage + 1 }))}
                            >
                                Next
                                <ChevronRightIcon className="ml-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Modal */}
            {cancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCancelModal(null); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cancel Ride Offer</h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            Cancel ride from <span className="font-medium text-slate-700 dark:text-slate-300">{cancelModal.origin}</span> to{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-300">{cancelModal.destination}</span>?
                            This will also cancel all pending bookings.
                        </p>
                        <textarea
                            rows={3}
                            placeholder="Reason for cancellation..."
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCancelModal(null); setActionReason(""); }}>
                                Go Back
                            </Button>
                            <Button
                                size="sm"
                                className="bg-rose-600 hover:bg-rose-700"
                                disabled={!actionReason.trim() || cancelMutation.isPending}
                                onClick={() => cancelMutation.mutate({ id: cancelModal.id, reason: actionReason })}
                            >
                                {cancelMutation.isPending ? "Cancelling..." : "Cancel Ride"}
                            </Button>
                        </div>
                        {cancelMutation.isError && (
                            <p className="mt-3 text-sm text-rose-600">{extractApiError(cancelMutation.error)}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Suspend/Resume Modal */}
            {suspendModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setSuspendModal(null); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            {suspendModal.isSuspended ? "Resume" : "Suspend"} Ride Offer
                        </h3>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            {suspendModal.isSuspended ? "Resume" : "Suspend"} ride from{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-300">{suspendModal.origin}</span> to{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-300">{suspendModal.destination}</span>?
                        </p>
                        <textarea
                            rows={3}
                            placeholder="Reason (optional)..."
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setSuspendModal(null); setActionReason(""); }}>
                                Go Back
                            </Button>
                            <Button
                                size="sm"
                                className={suspendModal.isSuspended ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}
                                disabled={suspendMutation.isPending}
                                onClick={() => suspendMutation.mutate({ id: suspendModal.id, suspend: !suspendModal.isSuspended, reason: actionReason })}
                            >
                                {suspendMutation.isPending ? "Processing..." : suspendModal.isSuspended ? "Resume Ride" : "Suspend Ride"}
                            </Button>
                        </div>
                        {suspendMutation.isError && (
                            <p className="mt-3 text-sm text-rose-600">{extractApiError(suspendMutation.error)}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
